import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Plus, ArrowRight, MapPin, LogOut, Search, Truck, CheckCircle2, Clock, AlertCircle, Calendar, Box, ChevronDown, ChevronUp, Image as ImageIcon, Printer, XCircle, X, Pencil, IndianRupee, UserCircle, FileText, Download } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import { usePhotoLightbox } from '../components/PhotoLightbox';
import axios from 'axios';
import BaggageTagPrint, { triggerPrint, printBaggageTagInPopup } from '../components/BaggageTagPrint';
import { formatPickupSlot } from '../lib/pickupSlots';
import { formatPickupAddress, formatDeliveryAddress } from '../lib/addressFormat';
import { openRazorpayCheckout } from '../lib/razorpay';
import { formatInr } from '../lib/formatInr';
import { displayAmount, pendingAmountNote } from '../lib/bookingAmount';
import { GstSplitText } from '../components/GstSplitText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYMENT_PROVIDER = (process.env.REACT_APP_PAYMENT_PROVIDER || 'stripe').toLowerCase();

const STATUS_CONFIG = {
  BOOKED: { icon: Calendar, color: '#52525B', label: 'BOOKED' },
  NEW_BOOKING: { icon: Calendar, color: '#A5B4FC', label: 'NEW BOOKING' },
  PAID: { icon: CheckCircle2, color: '#00C853', label: 'PAID' },
  SCHEDULED: { icon: Calendar, color: '#FFB800', label: 'SCHEDULED' },
  PICKED_UP: { icon: Box, color: '#FFB800', label: 'PICKED UP' },
  IN_TRANSIT: { icon: Truck, color: '#FF4500', label: 'IN TRANSIT' },
  ARRIVED_DESTINATION: { icon: MapPin, color: '#00A651', label: 'ARRIVED AT DESTINATION' },
  OUT_FOR_DELIVERY: { icon: Truck, color: '#FF4500', label: 'OUT FOR DELIVERY' },
  DELIVERED: { icon: CheckCircle2, color: '#00C853', label: 'DELIVERED' },
  DELAYED: { icon: AlertCircle, color: '#DC2626', label: 'DELAYED' },
  DELAYED_PICKUP: { icon: AlertCircle, color: '#DC2626', label: 'PICKUP DELAYED' },
  DELAYED_DELIVERY: { icon: AlertCircle, color: '#DC2626', label: 'DELIVERY DELAYED' },
  REROUTED: { icon: Truck, color: '#A855F7', label: 'REROUTED' },
  CANCELLED_BY_CUSTOMER: { icon: XCircle, color: '#9CA3AF', label: 'CANCELLED' }
};

const CUSTOMER_CANCELLABLE = ['BOOKED', 'NEW_BOOKING', 'SCHEDULED', 'PAID'];
// Customer can edit until the bag is physically picked up.
const CUSTOMER_EDITABLE = ['BOOKED', 'NEW_BOOKING', 'PAID', 'SCHEDULED', 'DELAYED_PICKUP'];

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const refundRequested = searchParams.get('refund_requested');
  const editSaved = searchParams.get('edit_saved');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');
  const [expandedBookings, setExpandedBookings] = useState({});
  const [printingBooking, setPrintingBooking] = useState(null);
  const [customer, setCustomer] = useState(null);
  const { open: openPhoto, gallery: photoGallery } = usePhotoLightbox();

  // Cancel booking modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelIdentifier, setCancelIdentifier] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState('');

  // Print-by-tag modal
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printIdentifier, setPrintIdentifier] = useState('');
  const [printError, setPrintError] = useState('');
  const [printLoading, setPrintLoading] = useState(false);

  // Track-by-tag modal
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [trackIdentifier, setTrackIdentifier] = useState('');
  const [trackError, setTrackError] = useState('');

  // Profile-edit modal
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({ full_name: '', email: '', residential_address: '' });
  const [profileMobile, setProfileMobile] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);

  const openProfileModal = async () => {
    setProfileError('');
    setProfileSaved(false);
    setShowProfileModal(true);
    // Hydrate from cache first for instant paint, then refresh from server
    setProfileForm({
      full_name: localStorage.getItem('full_name') || '',
      email: localStorage.getItem('email') || '',
      residential_address: localStorage.getItem('residential_address') || '',
    });
    setProfileMobile(localStorage.getItem('user_mobile') || '');
    const uid = localStorage.getItem('user_id');
    if (uid) {
      try {
        const r = await axios.get(`${API}/users/${uid}`);
        const u = r.data || {};
        setProfileForm({
          full_name: u.full_name || u.name || '',
          email: u.email || '',
          residential_address: u.residential_address || '',
        });
        setProfileMobile(u.mobile_number || localStorage.getItem('user_mobile') || '');
      } catch (e) { console.warn('[dashboard] profile refresh failed — using cached values:', e); }
    }
  };

  const handleProfileSave = async () => {
    setProfileError('');
    setProfileSaved(false);
    if (!profileForm.full_name.trim()) return setProfileError('Full name is required');
    if (!profileForm.email.includes('@')) return setProfileError('Enter a valid email');
    if (!profileForm.residential_address.trim()) return setProfileError('Address is required');
    const uid = localStorage.getItem('user_id');
    if (!uid) return setProfileError('Session expired — please sign in again');
    setProfileSaving(true);
    try {
      const r = await axios.patch(`${API}/users/${uid}/profile`, profileForm);
      const u = r.data?.user || {};
      // Refresh local cache so other screens read the new values
      localStorage.setItem('full_name', u.full_name || profileForm.full_name);
      localStorage.setItem('email', u.email || profileForm.email);
      localStorage.setItem('residential_address', u.residential_address || profileForm.residential_address);
      setUserName(u.full_name || profileForm.full_name);
      setProfileSaved(true);
      setTimeout(() => setShowProfileModal(false), 900);
    } catch (e) {
      setProfileError(e?.response?.data?.detail || 'Could not save profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePrintReceipt = async (booking) => {
    let cust = customer;
    if (!cust) {
      const uid = localStorage.getItem('user_id');
      if (uid) {
        try {
          const r = await axios.get(`${API}/users/${uid}`);
          cust = r.data;
          setCustomer(cust);
        } catch (e) { console.warn('[dashboard] fetch customer for print failed:', e); }
      }
    }
    // Use the iframe-popup printer — bulletproof against parent app CSS.
    printBaggageTagInPopup(booking, cust);
  };

  // Send the customer to the booking-form in edit mode. Backend will figure
  // out price diff (top-up or refund) once they save.
  const handleEditBooking = (booking) => {
    navigate(`/luggage-form?edit=${booking.booking_id}`);
  };

  // Trigger top-up checkout for the outstanding diff after a paid edit.
  const [topUpLoadingId, setTopUpLoadingId] = useState(null);
  const handlePayTopUp = async (booking) => {
    setTopUpLoadingId(booking.booking_id);
    try {
      if (PAYMENT_PROVIDER === 'razorpay') {
        await openRazorpayCheckout({
          booking,
          purpose: 'top_up',
          onSuccess: () => {
            // Refresh bookings list — back to /dashboard
            window.location.href = '/dashboard';
          },
          onDismiss: () => setTopUpLoadingId(null),
        });
      } else {
        const res = await axios.post(`${API}/payments/top-up-checkout`, {
          booking_id: booking.booking_id,
          origin_url: window.location.origin
        });
        if (res.data?.url) {
          window.location.href = res.data.url;
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to start top-up payment';
      if (detail !== 'Payment cancelled') alert(detail);
      setTopUpLoadingId(null);
    }
  };

  const toggleExpand = (bookingId) => {
    setExpandedBookings(prev => ({ ...prev, [bookingId]: !prev[bookingId] }));
  };

  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    const name = localStorage.getItem('user_name');
    
    if (!userId) {
      navigate('/');
      return;
    }
    
    // Validate session against backend — clears stale localStorage and forces re-login
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/users/${userId}`);
        if (cancelled) return;
        setUserName(name || 'User');
        fetchBookings(userId);
      } catch (err) {
        // Invalid / expired session → clear and bounce to landing
        localStorage.clear();
        navigate('/');
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const fetchBookings = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${API}/bookings/user/${userId}`);
      setBookings(response.data.bookings || []);
    } catch (err) {
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleNewBooking = () => navigate('/luggage-form');
  const handleTrack = (tag) => navigate(`/track?tag=${tag}`);

  const openCancelModal = (prefill = '') => {
    setCancelIdentifier(prefill);
    setCancelReason('');
    setCancelError('');
    setCancelSuccess('');
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    if (cancelLoading) return;
    setShowCancelModal(false);
  };

  const handleCancelSubmit = async () => {    setCancelError('');
    setCancelSuccess('');
    if (!cancelIdentifier.trim()) {
      setCancelError('Please enter the baggage tag');
      return;
    }
    if (!cancelReason.trim()) {
      setCancelError('Please provide a cancellation reason');
      return;
    }
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      navigate('/');
      return;
    }
    setCancelLoading(true);
    try {
      const res = await axios.post(`${API}/bookings/cancel-by-customer`, {
        user_id: userId,
        identifier: cancelIdentifier.trim(),
        reason: cancelReason.trim()
      });
      let msg = `Booking ${res.data.baggage_tag} cancelled successfully.`;
      if (res.data.refund_pending) {
        msg += ' Our team will process your refund shortly.';
      }
      setCancelSuccess(msg);
      await fetchBookings(userId);
      setTimeout(() => setShowCancelModal(false), 2500);
    } catch (err) {
      setCancelError(err.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelLoading(false);
    }
  };

  const openPrintModal = () => {
    setPrintIdentifier('');
    setPrintError('');
    setShowPrintModal(true);
  };

  const closePrintModal = () => {
    if (printLoading) return;
    setShowPrintModal(false);
  };

  const handlePrintByTag = async () => {
    setPrintError('');
    const ident = printIdentifier.trim();
    if (!ident) {
      setPrintError('Please enter the baggage tag');
      return;
    }
    const match = bookings.find(
      (b) => (b.baggage_tag || '').toLowerCase() === ident.toLowerCase()
        || (b.booking_id || '').toLowerCase() === ident.toLowerCase()
    );
    if (!match) {
      setPrintError(`No booking found in your account with baggage tag "${ident}"`);
      return;
    }
    setPrintLoading(true);
    try {
      await handlePrintReceipt(match);
      setShowPrintModal(false);
    } finally {
      setPrintLoading(false);
    }
  };

  const openTrackModal = () => {
    setTrackIdentifier('');
    setTrackError('');
    setShowTrackModal(true);
  };

  const closeTrackModal = () => setShowTrackModal(false);

  const handleTrackByTag = () => {
    setTrackError('');
    const ident = trackIdentifier.trim();
    if (!ident) {
      setTrackError('Please enter the baggage tag');
      return;
    }
    setShowTrackModal(false);
    navigate(`/track?tag=${encodeURIComponent(ident)}`);
  };

  if (loading) {
    return (
      <div className="theme-sunset flex items-center justify-center">
        <div className="text-center">
          <div className="loading-pulse font-mono text-lg">LOADING YOUR BOOKINGS...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-sunset p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-8 flex-wrap sm:flex-nowrap">
          <div className="flex items-center gap-3 min-w-0 flex-shrink">
            <BrandMark size={36} textClass="text-sm sm:text-base truncate" />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="profile-btn"
              onClick={openProfileModal}
              className="btn-secondary p-2 sm:px-4 sm:py-2 flex items-center gap-2 text-sm"
              aria-label="Edit profile"
            >
              <UserCircle size={16} />
              <span className="hidden sm:inline">PROFILE</span>
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="btn-secondary p-2 sm:px-4 sm:py-2 flex items-center gap-2 text-sm"
              aria-label="Log out"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">LOGOUT</span>
            </button>
          </div>
        </div>

        {(refundRequested || editSaved) && (
          <div
            className="mb-6 p-4 border-2 border-black bg-[#E6F4EA] flex items-start justify-between gap-3"
            data-testid="dashboard-edit-toast"
          >
            <div>
              <div className="text-xs font-mono uppercase font-bold mb-1">BOOKING UPDATED</div>
              <p className="text-xs font-mono leading-tight">
                {refundRequested
                  ? `Your booking has been updated. A refund of ${formatInr(refundRequested)} is pending admin approval — you'll receive a WhatsApp once it's processed.`
                  : 'Your booking changes have been saved.'}
              </p>
            </div>
            <button
              data-testid="dashboard-edit-toast-close"
              onClick={() => { searchParams.delete('refund_requested'); searchParams.delete('edit_saved'); setSearchParams(searchParams, { replace: true }); }}
              className="text-gray-700 hover:text-black"
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk', lineHeight: '1' }}>
              MY BOOKINGS
            </h1>
            <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">
              WELCOME, {userName.toUpperCase()}
            </p>
          </div>
          <div className="flex flex-col items-stretch md:items-end gap-2">
            <button
              data-testid="new-booking-btn"
              onClick={handleNewBooking}
              className="btn-primary px-6 py-3 flex items-center justify-center gap-2"
            >
              <Plus size={20} />
              NEW BOOKING
            </button>
            {bookings.length > 0 && (
              <div className="flex gap-2 flex-wrap justify-end">
                <button
                  data-testid="track-by-tag-btn"
                  onClick={openTrackModal}
                  className="border-2 border-black text-black hover:bg-orange-500 hover:text-black px-3 py-1.5 text-xs font-mono font-bold uppercase flex items-center justify-center gap-1.5"
                >
                  <Search size={14} />
                  TRACK
                </button>
                <button
                  data-testid="print-by-tag-btn"
                  onClick={openPrintModal}
                  className="border-2 border-black text-black hover:bg-black hover:text-white px-3 py-1.5 text-xs font-mono font-bold uppercase flex items-center justify-center gap-1.5"
                >
                  <Printer size={14} />
                  PRINT
                </button>
                <button
                  data-testid="cancel-booking-btn"
                  onClick={() => openCancelModal()}
                  className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-3 py-1.5 text-xs font-mono font-bold uppercase flex items-center justify-center gap-1.5"
                >
                  <XCircle size={14} />
                  CANCEL BOOKING
                </button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 border-2 border-red-600 bg-red-50 text-red-600 font-mono text-sm">
            {error}
          </div>
        )}

        {/* Bookings List */}
        {bookings.length === 0 ? (
          <div className="card-brutalist p-12 text-center">
            <Package size={64} strokeWidth={1.5} color="#52525B" className="mx-auto mb-4" />
            <h2 className="text-2xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              NO BOOKINGS YET
            </h2>
            <p className="text-sm text-gray-600 font-mono mb-6">
              CREATE YOUR FIRST LUGGAGE BOOKING TO GET STARTED
            </p>
            <button
              data-testid="create-first-booking-btn"
              onClick={handleNewBooking}
              className="btn-primary px-6 py-3 inline-flex items-center gap-2"
            >
              CREATE BOOKING
              <ArrowRight size={20} />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="bookings-list">
            {bookings.map((booking) => {
              const status = booking.tracking_status || 'BOOKED';
              const StatusIcon = STATUS_CONFIG[status]?.icon || Clock;
              return (
                <div
                  key={booking.booking_id}
                  className="card-brutalist p-6 transition-transform hover:-translate-y-1"
                  data-testid={`booking-card-${booking.baggage_tag}`}
                >
                  {/* Status Badge */}
                  <div className="flex items-center justify-between mb-4 pb-4 border-b-2 border-dashed border-gray-300">
                    <div className="text-xs font-mono uppercase text-gray-600">
                      {new Date(booking.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric'
                      })}
                    </div>
                    <div
                      className="flex items-center gap-2 px-3 py-1 border-2 border-black"
                      style={{ backgroundColor: STATUS_CONFIG[status]?.color }}
                    >
                      <StatusIcon size={14} color="#000" />
                      <span className="font-bold uppercase text-xs">
                        {STATUS_CONFIG[status]?.label}
                      </span>
                    </div>
                  </div>

                  {/* Baggage Tag */}
                  <div className="mb-4">
                    <div className="data-label mb-1">BAGGAGE TAG</div>
                    <div className="text-2xl font-black font-mono">{booking.baggage_tag}</div>
                  </div>

                  {/* Route */}
                  <div className="mb-4">
                    <div className="data-label mb-1 flex items-center gap-1">
                      <MapPin size={12} />
                      ROUTE
                    </div>
                    <div className="text-sm font-mono">
                      <div className="font-bold">{booking.pickup_location || 'N/A'}</div>
                      <div className="text-xs text-gray-600 break-words" data-testid={`pickup-full-${booking.baggage_tag}`}>{formatPickupAddress(booking)}</div>
                      <div className="text-orange-600 font-bold mt-1">↓</div>
                      <div className="font-bold">{booking.delivery_location}</div>
                      {formatDeliveryAddress(booking) && (
                        <div className="text-xs text-gray-600 break-words" data-testid={`delivery-full-${booking.baggage_tag}`}>{formatDeliveryAddress(booking)}</div>
                      )}
                    </div>
                  </div>

                  {/* Scheduled Pickup — after PICKED_UP, flip the label to past-tense
                      and show it as a "PICKED UP" confirmation instead of an
                      upcoming schedule. */}
                  {(booking.scheduled_pickup_date || booking.scheduled_pickup_time) && (() => {
                    const st = (booking.tracking_status || '').toUpperCase();
                    const isDone = ['PICKED_UP', 'IN_TRANSIT', 'ARRIVED_DESTINATION', 'OUT_FOR_DELIVERY', 'DELIVERED', 'REROUTED', 'DELAYED_DELIVERY'].includes(st);
                    return (
                      <div
                        className={`mb-4 p-2 border-2 border-black ${isDone ? 'bg-[#E6F9EE]' : 'bg-[#FFF7E6]'}`}
                        data-testid={`scheduled-pickup-${booking.baggage_tag}`}
                      >
                        <div className="data-label mb-1 flex items-center gap-1 text-[10px]">
                          {isDone ? <CheckCircle2 size={10} color="#00A651" /> : <Calendar size={10} />}
                          {isDone ? 'PICKED UP' : 'SCHEDULED PICKUP'}
                        </div>
                        <div className="text-xs font-bold font-mono">
                          {isDone
                            ? `Picked up on ${booking.scheduled_pickup_date || '—'}${booking.scheduled_pickup_time ? ` · ${formatPickupSlot(booking.scheduled_pickup_time)}` : ''}`
                            : `${booking.scheduled_pickup_date || '—'}${booking.scheduled_pickup_time ? ` · ${formatPickupSlot(booking.scheduled_pickup_time)}` : ''}`}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Details Grid */}
                  <div className="grid grid-cols-3 gap-2 mb-4 pb-4 border-b-2 border-dashed border-gray-300">
                    <div>
                      <div className="data-label mb-1 text-[10px]">PIECES</div>
                      <div className="data-value text-sm">{booking.luggage_details.quantity}</div>
                    </div>
                    <div>
                      <div className="data-label mb-1 text-[10px]">WEIGHT</div>
                      <div className="data-value text-sm">{booking.luggage_details.weight} KG</div>
                    </div>
                    <div>
                      <div className="data-label mb-1 text-[10px]">AMOUNT</div>
                      <div className="data-value text-sm" data-testid={`dashboard-amount-${booking.baggage_tag}`}>{formatInr(displayAmount(booking))}</div>
                      {pendingAmountNote(booking) && (
                        <div className="text-[10px] font-mono text-gray-600 leading-tight mt-0.5" data-testid={`dashboard-amount-note-${booking.baggage_tag}`}>{pendingAmountNote(booking)}</div>
                      )}
                    </div>
                  </div>

                  {/* Bags Detail (collapsible) */}
                  {(booking.luggage_details.bags || []).length > 0 && (
                    <div className="mb-3">
                      <button
                        type="button"
                        data-testid={`expand-bags-${booking.baggage_tag}`}
                        onClick={() => toggleExpand(booking.booking_id)}
                        className="w-full flex items-center justify-between px-3 py-2 border-2 border-black bg-white hover:bg-gray-100 text-xs font-mono uppercase font-bold"
                      >
                        <span>
                          {expandedBookings[booking.booking_id] ? 'HIDE' : 'VIEW'} BAG DETAILS ({booking.luggage_details.bags.length})
                        </span>
                        {expandedBookings[booking.booking_id] ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                      </button>
                      {expandedBookings[booking.booking_id] && (
                        <div className="mt-3 space-y-3" data-testid={`bags-detail-${booking.baggage_tag}`}>
                          {booking.luggage_details.bags.map((bag, idx) => (
                            <div key={idx} className="border-2 border-black p-3 bg-gray-50">
                              <div className="flex items-center justify-between mb-2 pb-1 border-b-2 border-dashed border-gray-300">
                                <div className="font-mono font-black text-xs">BAG #{idx + 1}</div>
                                <div className="px-2 py-0.5 bg-black text-white text-[10px] font-mono uppercase">
                                  {(bag.bag_type || 'BAG').toUpperCase()}
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <div className="data-label text-[10px]">COLOR</div>
                                  <div className="font-bold uppercase">{bag.color || '—'}</div>
                                </div>
                                <div>
                                  <div className="data-label text-[10px]">COMPANY/BRAND</div>
                                  <div className="font-bold uppercase">{bag.brand || '—'}</div>
                                </div>
                                <div>
                                  <div className="data-label text-[10px]">WEIGHT</div>
                                  <div className="font-bold">{bag.weight} KG</div>
                                </div>
                                <div>
                                  <div className="data-label text-[10px]">DIMENSIONS</div>
                                  <div className="font-bold">{bag.dimensions || '—'}</div>
                                </div>
                              </div>
                              {(bag.images || []).length > 0 && (
                                <div className="mt-2 pt-2 border-t border-dashed border-gray-300">
                                  <div className="data-label text-[10px] flex items-center gap-1 mb-1">
                                    <ImageIcon size={9}/>
                                    PHOTOS ({bag.images.length})
                                  </div>
                                  <div className="grid grid-cols-4 gap-1">
                                    {bag.images.map((img, imgIdx) => (
                                      <button
                                        key={imgIdx}
                                        type="button"
                                        onClick={() => openPhoto(bag.images, imgIdx)}
                                        data-testid={`dashboard-bag-${booking.baggage_tag}-${idx}-photo-${imgIdx}`}
                                        className="block border border-black overflow-hidden hover:opacity-80 cursor-zoom-in"
                                        title="Click to enlarge"
                                      >
                                        <img src={img} alt={`Bag ${idx + 1} photo ${imgIdx + 1}`} className="w-full h-12 object-cover" />
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment-state banners */}
                  {booking.payment_status === 'top_up_pending' && (
                    <div
                      className="mb-3 p-3 border-2 border-black bg-[#FFF3CD]"
                      data-testid={`top-up-banner-${booking.baggage_tag}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle size={14} color="#DC2626" />
                        <span className="text-xs font-mono uppercase font-bold">PAYMENT REQUIRED</span>
                      </div>
                      <p className="text-xs font-mono leading-tight">
                        You edited this booking. Pay <strong>{formatInr(booking.top_up_amount)}</strong>{' '}
                        <GstSplitText split={booking.top_up_breakdown} testId={`dashboard-topup-split-${booking.baggage_tag}`} />{' '}
                        to confirm — pickup will not proceed until payment is received.
                      </p>
                      <button
                        data-testid={`pay-top-up-btn-${booking.baggage_tag}`}
                        onClick={() => handlePayTopUp(booking)}
                        disabled={topUpLoadingId === booking.booking_id}
                        className="btn-primary w-full mt-2 py-2 text-xs flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <IndianRupee size={12} />
                        {topUpLoadingId === booking.booking_id ? 'OPENING…' : `PAY ${formatInr(booking.top_up_amount)} NOW`}
                      </button>
                    </div>
                  )}
                  {booking.payment_status === 'refund_pending' && (
                    <div
                      className="mb-3 p-3 border-2 border-black bg-[#E6F4EA]"
                      data-testid={`refund-banner-${booking.baggage_tag}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 size={14} color="#0F766E" />
                        <span className="text-xs font-mono uppercase font-bold">REFUND PENDING</span>
                      </div>
                      <p className="text-xs font-mono leading-tight">
                        You edited this booking. A refund of <strong>₹{Number(booking.pending_refund_amount || 0).toFixed(0)}</strong> has been requested and will be processed by our team shortly.
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    {CUSTOMER_EDITABLE.includes(booking.tracking_status || 'BOOKED') && (
                      <button
                        data-testid={`edit-booking-btn-${booking.baggage_tag}`}
                        onClick={() => handleEditBooking(booking)}
                        className="btn-secondary w-full py-2 text-xs flex items-center justify-center gap-1"
                        title="Edit booking — change bags, pickup, schedule"
                      >
                        <Pencil size={12} />
                        EDIT BOOKING
                      </button>
                    )}
                    {(booking.tracking_status || '').toUpperCase() === 'DELIVERED' && (
                      <button
                        data-testid={`download-invoice-btn-${booking.baggage_tag}`}
                        onClick={async () => {
                          try {
                            const userId = localStorage.getItem('user_id');
                            const resp = await axios.get(
                              `${API}/bookings/${booking.booking_id}/invoice/pdf`,
                              { params: { user_id: userId }, responseType: 'blob' }
                            );
                            const cd = resp.headers?.['content-disposition'] || '';
                            const match = cd.match(/filename="?([^";]+)"?/i);
                            const fname = match ? match[1] : `LFJ-Invoice-${booking.baggage_tag || booking.booking_id}.pdf`;
                            const blob = new Blob([resp.data], { type: 'application/pdf' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = fname;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                          } catch (err) {
                            let msg = 'Could not download invoice';
                            if (err?.response?.data instanceof Blob) {
                              try { msg = JSON.parse(await err.response.data.text())?.detail || msg; } catch { /* ignore */ }
                            } else {
                              msg = err?.response?.data?.detail || msg;
                            }
                            alert(msg);
                          }
                        }}
                        className="btn-secondary w-full py-2 text-xs flex items-center justify-center gap-1"
                        title="Download the GST tax invoice as a PDF"
                      >
                        <Download size={12} />
                        DOWNLOAD INVOICE
                      </button>
                    )}
                    {(booking.tracking_status || '').toUpperCase() === 'DELIVERED' && (
                      <button
                        data-testid={`email-invoice-btn-${booking.baggage_tag}`}
                        onClick={async () => {
                          try {
                            const userId = localStorage.getItem('user_id');
                            const email = customer?.email;
                            if (!email) {
                              alert('Please add your email to your profile first, then try again.');
                              return;
                            }
                            const res = await axios.post(`${API}/bookings/${booking.booking_id}/invoice/email`, { user_id: userId });
                            if (res.data?.ok) {
                              alert(`Tax invoice ${res.data.invoice_number} emailed to ${res.data.sent_to || email}`);
                            }
                          } catch (err) {
                            alert(err?.response?.data?.detail || 'Could not send invoice email');
                          }
                        }}
                        className="btn-secondary w-full py-2 text-xs flex items-center justify-center gap-1"
                        title="Email me the GST tax invoice as PDF"
                      >
                        <FileText size={12} />
                        EMAIL INVOICE
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Printable baggage tag (hidden on screen, shown only when window.print() is triggered) */}
      {printingBooking && (
        <BaggageTagPrint booking={printingBooking.booking} customer={printingBooking.customer} />
      )}

      {/* Print By Tag Modal */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="card-brutalist bg-white p-6 md:p-8 max-w-md w-full" data-testid="print-by-tag-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black uppercase flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                <Printer size={22} />
                PRINT BAGGAGE TAG
              </h2>
              <button data-testid="print-modal-close" onClick={closePrintModal} className="hover:text-orange-600 disabled:opacity-50" disabled={printLoading}>
                <X size={22} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">
                  BAGGAGE TAG <span className="text-red-600">*</span>
                </label>
                <input
                  data-testid="print-identifier-input"
                  type="text"
                  value={printIdentifier}
                  onChange={(e) => setPrintIdentifier(e.target.value)}
                  placeholder="e.g. LFJ214254"
                  className="w-full"
                  disabled={printLoading}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handlePrintByTag(); }}
                />
              </div>

              {printError && (
                <p data-testid="print-error" className="text-red-600 text-sm font-mono p-2 border-2 border-red-600 bg-red-50">
                  {printError}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  data-testid="print-modal-cancel"
                  onClick={closePrintModal}
                  disabled={printLoading}
                  className="btn-secondary px-5 py-3 text-sm flex-1 disabled:opacity-50"
                >
                  CANCEL
                </button>
                <button
                  data-testid="print-confirm-btn"
                  onClick={handlePrintByTag}
                  disabled={printLoading}
                  className="btn-primary px-5 py-3 text-sm flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Printer size={16} />
                  {printLoading ? 'PREPARING…' : 'PRINT TAG'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Edit Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="card-brutalist bg-white p-6 md:p-8 max-w-md w-full my-8" data-testid="profile-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black uppercase flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                <UserCircle size={22} />
                MY PROFILE
              </h2>
              <button data-testid="profile-modal-close" onClick={() => setShowProfileModal(false)} className="hover:text-orange-600 disabled:opacity-50" disabled={profileSaving}>
                <X size={22} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-[#FFF7E6] border-2 border-black text-[11px] font-mono leading-snug">
              Your mobile number is your verified account identity and cannot be edited here. Contact support if you need to change it.
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">Mobile Number (locked)</label>
                <input
                  data-testid="profile-mobile-input"
                  type="text"
                  value={`+91 ${String(profileMobile || '').replace(/^\+?91/, '').replace(/^\+/, '')}`}
                  readOnly
                  className="w-full opacity-60 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">Full Name <span className="text-red-600">*</span></label>
                <input
                  data-testid="profile-name-input"
                  type="text"
                  value={profileForm.full_name}
                  onChange={(e) => setProfileForm((p) => ({ ...p, full_name: e.target.value }))}
                  className="w-full"
                  disabled={profileSaving}
                />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">Email <span className="text-red-600">*</span></label>
                <input
                  data-testid="profile-email-input"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm((p) => ({ ...p, email: e.target.value }))}
                  className="w-full"
                  disabled={profileSaving}
                />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">Residential Address <span className="text-red-600">*</span></label>
                <textarea
                  data-testid="profile-address-input"
                  value={profileForm.residential_address}
                  onChange={(e) => setProfileForm((p) => ({ ...p, residential_address: e.target.value }))}
                  rows={3}
                  className="w-full resize-none"
                  disabled={profileSaving}
                />
              </div>

              {profileError && (
                <p data-testid="profile-error" className="text-red-600 text-sm font-mono p-2 border-2 border-red-600 bg-red-50">
                  {profileError}
                </p>
              )}
              {profileSaved && (
                <p data-testid="profile-saved" className="text-green-800 text-sm font-mono p-2 border-2 border-green-700 bg-green-50">
                  Profile updated successfully.
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  data-testid="profile-modal-cancel"
                  onClick={() => setShowProfileModal(false)}
                  disabled={profileSaving}
                  className="btn-secondary px-5 py-3 text-sm flex-1 disabled:opacity-50"
                >
                  CANCEL
                </button>
                <button
                  data-testid="profile-save-btn"
                  onClick={handleProfileSave}
                  disabled={profileSaving}
                  className="btn-primary px-5 py-3 text-sm flex-1 disabled:opacity-50"
                >
                  {profileSaving ? 'SAVING…' : 'SAVE CHANGES'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Track By Tag Modal */}
      {showTrackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="card-brutalist bg-white p-6 md:p-8 max-w-md w-full" data-testid="track-by-tag-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black uppercase flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                <Search size={22} />
                TRACK BAGGAGE
              </h2>
              <button data-testid="track-modal-close" onClick={closeTrackModal} className="hover:text-orange-600">
                <X size={22} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">
                  BAGGAGE TAG <span className="text-red-600">*</span>
                </label>
                <input
                  data-testid="track-identifier-input"
                  type="text"
                  value={trackIdentifier}
                  onChange={(e) => setTrackIdentifier(e.target.value)}
                  placeholder="e.g. LFJ214254"
                  className="w-full"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleTrackByTag(); }}
                />
              </div>

              {trackError && (
                <p data-testid="track-error" className="text-red-600 text-sm font-mono p-2 border-2 border-red-600 bg-red-50">
                  {trackError}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  data-testid="track-modal-cancel"
                  onClick={closeTrackModal}
                  className="btn-secondary px-5 py-3 text-sm flex-1"
                >
                  CANCEL
                </button>
                <button
                  data-testid="track-confirm-btn"
                  onClick={handleTrackByTag}
                  className="btn-primary px-5 py-3 text-sm flex-1 flex items-center justify-center gap-2"
                >
                  <Search size={16} />
                  TRACK TAG
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Booking Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="card-brutalist bg-white p-6 md:p-8 max-w-md w-full" data-testid="cancel-booking-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black uppercase flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                <XCircle size={22} color="#DC2626" />
                CANCEL BOOKING
              </h2>
              <button data-testid="cancel-modal-close" onClick={closeCancelModal} className="hover:text-orange-600 disabled:opacity-50" disabled={cancelLoading}>
                <X size={22} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-[#FFF7E6] border-2 border-black text-xs font-mono">
              Cancellation is only allowed while the booking is in
              <span className="font-bold"> BOOKED</span>,
              <span className="font-bold"> NEW BOOKING</span>,
              <span className="font-bold"> PAID</span> or
              <span className="font-bold"> SCHEDULED</span> status.
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">
                  BAGGAGE TAG <span className="text-red-600">*</span>
                </label>
                <input
                  data-testid="cancel-identifier-input"
                  type="text"
                  value={cancelIdentifier}
                  onChange={(e) => setCancelIdentifier(e.target.value)}
                  placeholder="e.g. LFJ214254"
                  className="w-full"
                  disabled={cancelLoading}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">
                  REASON FOR CANCELLATION <span className="text-red-600">*</span>
                </label>
                <textarea
                  data-testid="cancel-reason-input"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Tell us why you're cancelling…"
                  rows={3}
                  className="w-full"
                  disabled={cancelLoading}
                />
              </div>

              {cancelError && (
                <p data-testid="cancel-error" className="text-red-600 text-sm font-mono p-2 border-2 border-red-600 bg-red-50">
                  {cancelError}
                </p>
              )}
              {cancelSuccess && (
                <p data-testid="cancel-success" className="text-green-700 text-sm font-mono p-2 border-2 border-green-700 bg-green-50">
                  {cancelSuccess}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  data-testid="cancel-modal-back"
                  onClick={closeCancelModal}
                  disabled={cancelLoading}
                  className="btn-secondary px-5 py-3 text-sm flex-1 disabled:opacity-50"
                >
                  KEEP BOOKING
                </button>
                <button
                  data-testid="cancel-confirm-btn"
                  onClick={handleCancelSubmit}
                  disabled={cancelLoading || !!cancelSuccess}
                  className="btn-primary px-5 py-3 text-sm flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#DC2626' }}
                >
                  <XCircle size={16} />
                  {cancelLoading ? 'CANCELLING...' : 'CONFIRM CANCEL'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {photoGallery}
    </div>
  );
};

export default Dashboard;
